import type { AttemptInput } from '@dcs/contracts';
import type { AttemptSender, SessionCheck } from '../queue';

/**
 * A body that does not satisfy the contract: no use sending it (the server would answer 422 and the
 * queue would drop it), so it fails here with the same shape. `status` 422 sends it to the dead-letter
 * list.
 */
export class InvalidAttemptError extends Error {
  override readonly name = 'InvalidAttemptError';
  readonly status = 422;
  readonly code = 'invalid_attempt';
  constructor(message: string) {
    super(message);
  }
}

export type SessionProbe = () => Promise<SessionCheck>;

export interface ApiGateway {
  readonly send: AttemptSender;
  /** Fresh `GET /api/me`: who is signed in now, `anonymous` on a 401, `error` if it cannot be told. */
  readonly session: SessionProbe;
}

/** How long a fresh session check may take before it counts as "cannot be told" (lie-fi). */
export const SESSION_TIMEOUT_MS = 8000;

const isStatusError = (error: unknown): error is { status: number } =>
  typeof error === 'object' &&
  error !== null &&
  typeof (error as { status?: unknown }).status === 'number';

/**
 * The real network side: `@dcs/api` (Hono client) and the contract's zod schema are imported on first
 * use, so neither is in the shell or in the `/ly-thuyet` chunk; a guest, or a signed-in user who never
 * finishes an exam, never downloads them. A failed chunk load (offline before the service worker
 * cached it) is reported as "no answer" so the queue keeps the item and tries again later.
 */
export function createApiGateway(timeoutMs: number = SESSION_TIMEOUT_MS): ApiGateway {
  type Loaded = Awaited<ReturnType<typeof load>>;
  let loaded: Promise<{
    save(input: AttemptInput): Promise<unknown>;
    /** `GET /api/me`, aborted through `signal` (its own client, so the signal reaches fetch). */
    me(signal: AbortSignal): Promise<{ id: string } | null>;
  }> | null = null;

  function load() {
    loaded ??= Promise.all([import('@dcs/api'), import('@dcs/contracts')]).then(
      ([{ createApiClient }, { AttemptInputSchema }]) => {
        const client = createApiClient();
        return {
          save(input: AttemptInput): Promise<unknown> {
            const parsed = AttemptInputSchema.safeParse(input);
            if (!parsed.success) {
              return Promise.reject(
                new InvalidAttemptError(parsed.error.issues[0]?.message ?? 'Invalid attempt'),
              );
            }
            return client.saveAttempt(parsed.data);
          },
          me: (signal) =>
            createApiClient({
              fetch: (input, init) => globalThis.fetch(input, { ...init, signal }),
            }).getMe(),
        };
      },
    );
    return loaded;
  }

  async function api(): Promise<Loaded> {
    try {
      return await load();
    } catch {
      loaded = null;
      throw Object.assign(new Error('Attempt sender could not load'), {
        status: 0,
        code: 'network_error',
      });
    }
  }

  return {
    async send(input) {
      return (await api()).save(input);
    },
    async session() {
      let timer: ReturnType<typeof setTimeout> | undefined;
      // The timeout also aborts the request (T-2.2 m-a): a hung lie-fi connection is not left open.
      const controller = new AbortController();
      try {
        const timeout = new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            reject(new Error('session check timed out'));
          }, timeoutMs);
        });
        const me = await Promise.race([(await api()).me(controller.signal), timeout]);
        return me === null ? { kind: 'anonymous' } : { kind: 'user', id: me.id };
      } catch (error) {
        // A banned user (403) cannot save anything: same as signed out. Everything else (offline,
        // 5xx, timeout, a proxy that is not there yet) says nothing about who is signed in.
        return isStatusError(error) && error.status === 403
          ? { kind: 'anonymous' }
          : { kind: 'error' };
      } finally {
        clearTimeout(timer);
      }
    },
  };
}
